import React, { ReactNode } from 'react';

import { Link } from '@klarna/bubble-ui';

import { ContainerTemplate } from '@/components';
import { PersonRole } from '@mjolnir/mf-fields-ts/src/enm/PersonRole';
import { OnboardingGroupComponentProps } from '@/bpc/components/OnboardingGroupComponent/OnboardingGroupComponent';

import { StakeholderSubGroupComponent } from './StakeholderSubGroupComponent';

interface StakeholderGroupComponentState {
  amountOfDeletedItems: number;
  currentRole: PersonRole;
}

export class StakeholderGroupComponent extends React.Component<
  OnboardingGroupComponentProps,
  StakeholderGroupComponentState
> {
  constructor(props: OnboardingGroupComponentProps) {
    super(props);

    const missingRoles = this.determineMissingSubgroups();
    const currentCollectedRoles = this.getCurrentCollectedRoles();
    const currentRole = currentCollectedRoles.length ? currentCollectedRoles[0] : missingRoles[0];

    this.state = {
      amountOfDeletedItems: 0,
      currentRole,
    };

    this.onPreviousRole = this.onPreviousRole.bind(this);
    this.hasMissingRoles = this.hasMissingRoles.bind(this);
    this.hasNextRole = this.hasNextRole.bind(this);
    this.hasPreviousRole = this.hasPreviousRole.bind(this);
    this.getCurrentRoleFields = this.getCurrentRoleFields.bind(this);
    this.getPreviousRole = this.getPreviousRole.bind(this);
    this.getNextRole = this.getNextRole.bind(this);
    this.determineMissingSubgroups = this.determineMissingSubgroups.bind(this);
    this.getCurrentCollectedRoles = this.getCurrentCollectedRoles.bind(this);
    this.onNext = this.onNext.bind(this);
  }

  private determineMissingSubgroups(): PersonRole[] | [] {
    const { group } = this.props;
    return (group.determineMissingSubgroups?.() as PersonRole[]) || [];
  }

  private getCurrentCollectedRoles(): Array<PersonRole> {
    const { group } = this.props;

    const persons =
      group.payload.business_information?.persons ||
      group.payload.mor_business_information?.persons;

    if (!persons) return [];

    const collectedRoles = persons
      .map(p => p.person_role)
      .flat()
      .filter((role, index, array) => array.indexOf(role) === index);

    return collectedRoles as Array<PersonRole>;
  }

  private getMissingRoles(): Array<PersonRole> {
    return this.determineMissingSubgroups() as Array<PersonRole>;
  }

  private getPreviousRole(): PersonRole | null {
    const { currentRole } = this.state;
    const { group } = this.props;
    const currentCollectedRoles = this.getCurrentCollectedRoles();
    const missingRoles = this.getMissingRoles();

    const currentRoleIndex = currentCollectedRoles.indexOf(currentRole);
    const missingRoleIndex = missingRoles.indexOf(currentRole);

    const isMorOnboarding =
      group.payload?.contract_onboarding_type === 'MOR_ONBOARDING_GROUP' ||
      group.payload?.contract_onboarding_type === 'MOR_ONBOARDING_ENTITY';

    if (missingRoleIndex > 0) {
      return missingRoles[missingRoleIndex - 1];
    } else if (currentRoleIndex > 0) {
      return currentCollectedRoles[currentRoleIndex - 1];
    } else {
      return isMorOnboarding ? null : currentCollectedRoles[currentCollectedRoles.length - 1];
    }
  }

  private onPreviousRole(e): void {
    e.preventDefault();
    const previousRole = this.getPreviousRole();

    if (previousRole === null && this.props.hasPreviousGroup) {
      this.props.onPreviousGroup();
      return;
    }

    if (previousRole !== null) {
      this.setState({
        currentRole: previousRole,
      });
    }
  }

  private getNextRole(): PersonRole {
    const { currentRole } = this.state;
    const currentCollectedRoles = this.getCurrentCollectedRoles();
    const currentRoleIndex = currentCollectedRoles.indexOf(currentRole) || 0;
    const missingRoles = this.getMissingRoles();

    if (currentRoleIndex !== -1 && currentRoleIndex < currentCollectedRoles.length - 1) {
      return currentCollectedRoles[currentRoleIndex + 1];
    } else if (missingRoles.indexOf(currentRole) !== -1) {
      return missingRoles[missingRoles.indexOf(currentRole) + 1];
    } else {
      return missingRoles[0];
    }
  }

  private onNextRole(e) {
    e.preventDefault();

    const { onNextSubgroup } = this.props;
    const nextRole = this.getNextRole();

    this.setState({
      currentRole: nextRole,
    });

    return onNextSubgroup();
  }

  private hasMissingRoles() {
    const missingRoles = this.getMissingRoles();
    return missingRoles.length && missingRoles[missingRoles.length - 1] !== this.state.currentRole;
  }

  private hasNextRole() {
    const { currentRole } = this.state;
    const currentRoles = this.getCurrentCollectedRoles();
    return (
      this.hasMissingRoles() ||
      (currentRoles.indexOf(currentRole) < currentRoles.length - 1 &&
        currentRoles.indexOf(currentRole) !== -1)
    );
  }

  private hasPreviousRole() {
    const { currentRole } = this.state;
    const currentRoles = this.getCurrentCollectedRoles();
    const missingRoles = this.getMissingRoles();
    return (
      (currentRoles.length > 0 && currentRoles.indexOf(currentRole) > 0) ||
      (missingRoles.length > 0 && missingRoles.indexOf(currentRole) > 0) ||
      (missingRoles.indexOf(currentRole) === 0 && currentRoles.length > 0)
    );
  }

  private getCurrentRoleFields(currentRole: PersonRole) {
    const { group } = this.props;

    return group.fields.filter(fg =>
      fg.some(
        f =>
          f.absolutePath.endsWith('person_role') &&
          (!group.getValueForField(f.absolutePath) ||
            (group.getValueForField(f.absolutePath) as Array<PersonRole>).includes(currentRole)),
      ),
    );
  }

  private onNext(e) {
    if (this.hasNextRole()) {
      this.onNextRole(e);
    } else if (this.props.hasNextGroup) {
      this.props.onNextGroup();
    } else {
      this.props.onShowSummaryPage();
    }
  }

  shouldComponentUpdate(
    nextProps: Readonly<OnboardingGroupComponentProps>,
    nextState: Readonly<StakeholderGroupComponentState>,
  ): boolean {
    return (
      this.state.amountOfDeletedItems !== nextState.amountOfDeletedItems ||
      this.props.group.groupId !== nextProps.group.groupId ||
      this.props.hasNextGroup !== nextProps.hasNextGroup ||
      this.props.hasPreviousGroup !== nextProps.hasPreviousGroup ||
      this.state.currentRole !== nextState.currentRole
    );
  }

  render(): ReactNode {
    const {
      error,
      group,
      hasPreviousGroup,
      onPreviousGroup,
      isLoading,
      t,
      tKey,
      tArray,
    } = this.props;
    const { currentRole } = this.state;

    const hasNextRole = this.hasNextRole();
    const hasPreviousRole = this.hasPreviousRole();
    const currentRoleFields = this.getCurrentRoleFields(currentRole);

    return (
      <ContainerTemplate
        title={t(`group.stakeholders.${currentRole}.title`) || group.fallbackTitle}
        subtitle={
          tArray(`group.stakeholders.${currentRole}.subtitle`)?.map(({ text, link }, index) =>
            link ? (
              <Link key={index} href={link as string} target="_blank">
                {text}
              </Link>
            ) : (
              <span key={index}>{text}</span>
            ),
          ) || group.fallbackSubtitle
        }
        error={error}
      >
        <StakeholderSubGroupComponent
          canGoBack={hasPreviousGroup || hasPreviousRole}
          canSkipRole={hasNextRole}
          forceUpdate={(): void => this.forceUpdate()}
          group={group}
          isLoading={isLoading}
          onBack={hasPreviousRole ? this.onPreviousRole : onPreviousGroup}
          onNext={this.onNext}
          role={currentRole}
          stakeholderItems={currentRoleFields}
          max={group.maxItems}
          min={group.minItems}
          t={t}
          tKey={tKey}
        />
      </ContainerTemplate>
    );
  }
}
